// Mirror of src/search/schema.ts on the backend.
// Keep these in sync manually — the backend Zod schema is the source of truth.

export type HopTierId = "hop0" | "hop1" | "hop2" | "hop3";

export type Confidence = "low" | "medium" | "high";

export type SearchRequest = {
  query: string;
  limit_nodes?: number;
  limit_edges?: number;
  include_claims?: boolean;
  include_sources?: boolean;
  edge_types?: string[];
  min_confidence?: Confidence;
};

export type SearchNode = {
  id: string;
  slug: string;
  title: string;
  type: string;
  summary: string | null;
  confidence: Confidence;
  quality_score: number;
  depth: number;
  score: number;
};

export type SearchEdge = {
  id: string;
  source_node_id: string;
  target_node_id: string;
  edge_type: string;
  claim: string | null;
  confidence: Confidence;
  quality_score: number;
  source_id: string | null;
  contributor_id: string | null;
};

export type SearchClaim = {
  id: string;
  node_id: string | null;
  edge_id: string | null;
  text: string;
  confidence: Confidence;
  status: string;
  source_id: string | null;
  contributor_id: string | null;
};

export type SearchSource = {
  id: string;
  title: string | null;
  url: string | null;
  content_sha256: string | null;
};

export type SearchAttribution = {
  item_type: "node" | "edge" | "claim" | "source";
  item_id: string;
  contributor_id: string | null;
  contributor_wallet: string | null;
  weight: number;
};

export type SearchResponse = {
  query: string;
  tier: HopTierId;
  hops_charged: number;
  paid_mode: boolean;
  paid_call_id?: string | null;
  nodes: SearchNode[];
  edges: SearchEdge[];
  claims: SearchClaim[];
  sources: SearchSource[];
  attribution: SearchAttribution[];
  usage: {
    nodes_returned: number;
    edges_returned: number;
    claims_returned: number;
    sources_returned: number;
  };
  warnings: string[];
};

export type DatabaseStatus = "up" | "not_configured" | "down";

export type HealthResponse = {
  ok: boolean;
  service: string;
  paywrap: "enabled" | "disabled";
  database: DatabaseStatus;
};

export type AuthNonceResponse = {
  wallet: string;
  nonce: string;
  message: string;
  expires_at: string;
};

export type AuthSessionResponse = {
  wallet: string;
  token: string;
  expires_at: string;
};

export type MaterializationRequest = {
  source_id?: string;
  url?: string;
  title?: string;
  access_method_id?: string;
  provider?: "zero" | "direct" | "publisher" | "user_upload";
  provider_run_id?: string;
  canonical_url?: string;
  content_type?: "markdown" | "text" | "html" | "json";
  markdown?: string;
  text_content?: string;
  raw_json?: Record<string, unknown>;
  cache_policy?:
    | "private_wallet_cache"
    | "metadata_only"
    | "do_not_store"
    | "licensed_full_text";
  license_status?:
    | "unknown"
    | "public_domain"
    | "permissive"
    | "publisher_license"
    | "user_provided"
    | "copyrighted";
  allowed_outputs?: {
    public_search?: "metadata_only" | "short_snippets" | "full_text";
    wallet_search?: "metadata_only" | "short_snippets" | "full_text";
  };
  metadata?: Record<string, unknown>;
  fetched_at?: string;
  expires_at?: string | null;
};

export type MaterializationSummary = {
  id: string;
  source_id: string;
  owner_wallet: string;
  provider: string;
  provider_run_id: string | null;
  canonical_url: string | null;
  title: string | null;
  content_type: string;
  content_sha256: string;
  cache_policy: string;
  license_status: string;
  fetched_at: string;
  expires_at: string | null;
  created_at: string;
};

export type MaterializationDetail = MaterializationSummary & {
  markdown: string | null;
  text_content: string | null;
  raw_json: Record<string, unknown>;
  allowed_outputs: Record<string, unknown>;
  metadata: Record<string, unknown>;
};

export type SourceAccessMethod = {
  provider: "zero" | "direct" | "publisher" | "user_upload";
  label: string;
  description: string;
  zero_capability_slug: string | null;
  price_usdc_micro: number | null;
  command: string | null;
  cache_policy: MaterializationRequest["cache_policy"];
  license_status: MaterializationRequest["license_status"];
  allowed_outputs: NonNullable<MaterializationRequest["allowed_outputs"]>;
};

export type SourceAccessResponse = {
  url: string;
  methods: SourceAccessMethod[];
  warnings: string[];
};

export type HopTierMeta = {
  id: HopTierId;
  hops: number;
  label: string;
  priceUsd: number;
  priceLabel: string;
  description: string;
  caps: { maxNodes: number; maxEdges: number; maxClaims: number };
};

// Static mirror of HOP_TIERS from src/pricing.ts. Hardcoded so the frontend
// can render pricing/caps without a network round trip.
export const HOP_TIERS: HopTierMeta[] = [
  {
    id: "hop0",
    hops: 0,
    label: "Hop 0 — Matched nodes",
    priceUsd: 0.002,
    priceLabel: "$0.002",
    description: "Just the nodes that match your query.",
    caps: { maxNodes: 20, maxEdges: 0, maxClaims: 20 },
  },
  {
    id: "hop1",
    hops: 1,
    label: "Hop 1 — Direct neighbors",
    priceUsd: 0.006,
    priceLabel: "$0.006",
    description: "Matches plus everything one edge away.",
    caps: { maxNodes: 40, maxEdges: 80, maxClaims: 40 },
  },
  {
    id: "hop2",
    hops: 2,
    label: "Hop 2 — Local subgraph",
    priceUsd: 0.015,
    priceLabel: "$0.015",
    description: "Two-hop neighborhood with claims and sources.",
    caps: { maxNodes: 75, maxEdges: 180, maxClaims: 75 },
  },
  {
    id: "hop3",
    hops: 3,
    label: "Hop 3 — Research map",
    priceUsd: 0.04,
    priceLabel: "$0.040",
    description: "Three-hop expanded research map.",
    caps: { maxNodes: 120, maxEdges: 360, maxClaims: 120 },
  },
];
